import { Suggestion, SuggestionGroup, SuggestionValue, ListingData } from 'src/models/listing-optimizer.model';

export type PhotoValue = Extract<SuggestionValue, { type: 'photo' }>;

export interface PhotoChangeVM {
  suggestion: Suggestion;
  kind: string;
}

export interface PhotoAssetVM {
  key: string;
  assetLabel: string;
  hero: PhotoValue | null;
  changes: PhotoChangeVM[];
}

export interface SuggestionGroupVM {
  key: SuggestionGroup;
  label: string;
  icon: string;
  isPhotos: boolean;
  cards: Suggestion[];
  photoAssets: PhotoAssetVM[];
}

// Amenities are excluded — they render as a plain "add these" list, not as cards.
const CARD_GROUP_ORDER: SuggestionGroup[] = ['title', 'description', 'photos'];

const GROUP_LABELS: Record<SuggestionGroup, string> = {
  title: 'Title',
  description: 'Description',
  photos: 'Photos',
  amenities: 'Amenities'
};

const GROUP_ICONS: Record<SuggestionGroup, string> = {
  title: 'assets/svg/document-text-outline.svg',
  description: 'assets/svg/document-text-outline.svg',
  photos: 'assets/svg/camera-outline.svg',
  amenities: 'assets/svg/star-outline.svg'
};

// The API prefixes some card elements with their section name (for example,
// "Description · About this space"). The section heading already provides
// that context, so keep only the useful, specific part inside the card.
export function suggestionCardLabel(s: Suggestion): string {
  const sectionLabel = GROUP_LABELS[s.group];
  const element = (s.element || '').trim();
  if (element.toLocaleLowerCase() === sectionLabel.toLocaleLowerCase()) {return '';}

  const escapedSection = sectionLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return element.replace(new RegExp(`^${escapedSection}\\s*[·:|–—-]\\s*`, 'i'), '').trim();
}

type AbsoluteUrl = (url: string | undefined | null) => string;

// Only these suggestions can have their "after" generated on demand.
export function isGeneratable(s: Suggestion): boolean {
  return s.afterStatus === 'pending' && (
    s.id === 'title' ||
    s.id.startsWith('desc-') ||
    /^photo-\d+-edit$/.test(s.id) ||
    /^photo-\d+-staging-[a-z0-9_]+$/.test(s.id)
  );
}

// All `photo-<n>-*` suggestions share a photo asset; the asset key is the photo number.
export function photoAssetKey(id: string): string {
  const m = id.match(/^photo-(\d+)-/);
  return m ? m[1] : id;
}

export function photoChangeKind(id: string): string {
  if (/^photo-\d+-staging-/.test(id)) {return 'Staging idea';}
  if (id.includes('-caption')) {return 'Caption';}
  if (/-edit$/.test(id)) {return 'Photo edit';}
  if (id.includes('-order')) {return 'Reorder';}
  return 'Change';
}

// Missing curated amenities, deduped, as a plain name list ("Good"/already-listed ones dropped).
export function buildAmenityNames(suggestions: Suggestion[]): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const s of suggestions) {
    if (s.group !== 'amenities') {continue;}
    if (s.before.type === 'amenity' && s.before.listed) {continue;}
    const name = s.before.type === 'amenity'
      ? s.before.name
      : (s.after && s.after.type === 'amenity' ? s.after.name : s.element);
    if (name && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  }
  return names;
}

// Group every `photo-<n>-*` suggestion into one card per photo, ordered numerically.
export function buildPhotoAssets(
  list: Suggestion[],
  listingPhotos: ListingData['photos'],
  absoluteUrl: AbsoluteUrl
): PhotoAssetVM[] {
  const order: string[] = [];
  const byKey = new Map<string, Suggestion[]>();
  for (const s of list) {
    const k = photoAssetKey(s.id);
    if (!byKey.has(k)) {
      byKey.set(k, []);
      order.push(k);
    }
    byKey.get(k).push(s);
  }
  order.sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) {return na - nb;}
    return a.localeCompare(b);
  });

  const photos = listingPhotos || [];
  return order.map(k => {
    const group = byKey.get(k);
    let hero: PhotoValue | null = null;
    const heroSrc = group.find(x => x.before.type === 'photo')?.before;
    if (heroSrc && heroSrc.type === 'photo') {
      hero = heroSrc;
    }
    if (!hero) {
      const num = Number(k);
      if (Number.isFinite(num) && photos.length) {
        const photoIndex = num - 1;
        const match = photos.find(p => Number(p.index) === photoIndex) || photos[photoIndex];
        if (match?.url) {
          hero = { type: 'photo', url: absoluteUrl(match.url) };
        }
      }
    }
    return {
      key: k,
      assetLabel: group[0].element,
      hero,
      changes: group.map(s => ({ suggestion: s, kind: photoChangeKind(s.id) }))
    };
  });
}

// Title / Description / Photos groups, in canonical order, preserving within-group array order.
export function buildSuggestionGroups(
  suggestions: Suggestion[],
  listingPhotos: ListingData['photos'],
  absoluteUrl: AbsoluteUrl
): SuggestionGroupVM[] {
  const byGroup: Record<SuggestionGroup, Suggestion[]> = { title: [], description: [], photos: [], amenities: [] };
  for (const s of suggestions) {
    if (s.group === 'amenities') {continue;}
    byGroup[s.group].push(s);
  }
  const groups: SuggestionGroupVM[] = [];
  for (const key of CARD_GROUP_ORDER) {
    const list = byGroup[key];
    if (!list.length) {continue;}
    groups.push({
      key,
      label: GROUP_LABELS[key],
      icon: GROUP_ICONS[key],
      isPhotos: key === 'photos',
      cards: key === 'photos' ? [] : list,
      photoAssets: key === 'photos' ? buildPhotoAssets(list, listingPhotos, absoluteUrl) : []
    });
  }
  return groups;
}

export function buildSummaryLine(nonAmenityCount: number, amenityCount: number): string {
  if (nonAmenityCount > 0) {
    return `We found ${nonAmenityCount} ${nonAmenityCount === 1 ? 'suggestion' : 'suggestions'} to improve your listing.`;
  }
  return amenityCount > 0
    ? 'No fixes needed — see the top amenities below to boost revenue.'
    : 'Your listing is in great shape — no suggestions right now.';
}
